import { useMemo, useState, useCallback, type Dispatch, type SetStateAction } from 'react';
import type { TablePaginationConfig, TableProps } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';

export const DEFAULT_PAGE_SIZE = 10;

export interface PaginationState {
  current: number;
  pageSize: number;
  total: number;
}

export interface UseTableStateOptions<T> {
  defaultPageSize?: number;
  initialSorter?: SorterResult<T>;
  showSizeChanger?: boolean;
  onPageChange?: (offset: number, limit: number) => void;
}

export interface UseTableStateReturn<T> {
  /** Current pagination state */
  pagination: PaginationState;
  /** Set pagination state */
  setPagination: Dispatch<SetStateAction<PaginationState>>;
  /** Current sorter state */
  sorter: SorterResult<T>;
  /** Set sorter state */
  setSorter: Dispatch<SetStateAction<SorterResult<T>>>;
  /** Pagination config ready for Ant Design Table */
  tablePagination: TablePaginationConfig;
  /** Handler for Ant Design Table onChange event */
  handleTableChange: NonNullable<TableProps<T>['onChange']>;
  /** Reset pagination to first page */
  resetPagination: () => void;
  /** Update the total count */
  updateTotal: (total: number) => void;
  /** Update the current page */
  updateCurrentPage: (page: number) => void;
  /** Get current offset for API calls */
  getOffset: () => number;
  /** Calculate offset for a specific page */
  calculateOffset: (page: number) => number;
}

/**
 * Custom hook for managing table state including pagination and sorting.
 * Reduces boilerplate when using CardTable or Ant Design Table components.
 */
export function useTableState<T extends object>(
  options: UseTableStateOptions<T> = {}
): UseTableStateReturn<T> {
  const {
    defaultPageSize = DEFAULT_PAGE_SIZE,
    initialSorter = {} as SorterResult<T>,
    showSizeChanger = false,
    onPageChange,
  } = options;

  const [pagination, setPagination] = useState<PaginationState>({
    current: 1,
    pageSize: defaultPageSize,
    total: 0,
  });

  const [sorter, setSorter] = useState<SorterResult<T>>(initialSorter);

  const tablePagination = useMemo<TablePaginationConfig>(
    () => ({
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: pagination.total,
      showSizeChanger,
    }),
    [pagination.current, pagination.pageSize, pagination.total, showSizeChanger]
  );

  const handleTableChange: NonNullable<TableProps<T>['onChange']> = useCallback(
    (paginationConfig, _filters, sorterArg) => {
      const nextPage = paginationConfig?.current ?? pagination.current;
      const nextPageSize = paginationConfig?.pageSize ?? pagination.pageSize;
      const pageChanged = nextPage !== pagination.current || nextPageSize !== pagination.pageSize;

      setPagination((prev) => ({
        ...prev,
        current: nextPage,
        pageSize: nextPageSize,
      }));

      if (pageChanged && onPageChange) {
        const offset = (nextPage - 1) * nextPageSize;
        onPageChange(offset, nextPageSize);
      }

      const normalizedSorter = Array.isArray(sorterArg) ? sorterArg[0] : sorterArg;
      if (normalizedSorter && normalizedSorter.order) {
        setSorter(normalizedSorter as SorterResult<T>);
      } else {
        setSorter({} as SorterResult<T>);
      }
    },
    [pagination.current, pagination.pageSize, onPageChange]
  );

  const resetPagination = useCallback(() => {
    setPagination((prev) => ({ ...prev, current: 1, total: 0 }));
    setSorter({} as SorterResult<T>);
  }, []);

  const updateTotal = useCallback((total: number) => {
    setPagination((prev) => ({ ...prev, total }));
  }, []);

  const updateCurrentPage = useCallback((page: number) => {
    setPagination((prev) => ({ ...prev, current: page }));
  }, []);

  const getOffset = useCallback(() => {
    return (pagination.current - 1) * pagination.pageSize;
  }, [pagination.current, pagination.pageSize]);

  const calculateOffset = useCallback(
    (page: number) => {
      return (page - 1) * pagination.pageSize;
    },
    [pagination.pageSize]
  );

  return {
    pagination,
    setPagination,
    sorter,
    setSorter,
    tablePagination,
    handleTableChange,
    resetPagination,
    updateTotal,
    updateCurrentPage,
    getOffset,
    calculateOffset,
  };
}

export default useTableState;
